import React, { useEffect, useState, useCallback, useRef } from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';

/**
 * Iter-213 — Backend Health Banner.
 *
 * Iter-314 P0 REWRITE — "Checking backend connection…" was showing on
 * every login page mount, making the app feel unstable. The banner
 * now:
 *
 *   1. Probes `/api/health` SILENTLY on mount — no visible spinner
 *      unless the probe takes longer than 800 ms.
 *   2. Caches the last successful probe result in sessionStorage. A
 *      previously-healthy backend skips the probe on re-navigation
 *      within the same tab.
 *   3. Only surfaces the "Waking backend" banner if the probe fails
 *      OR takes longer than 800 ms — never on the fast path.
 *   4. Calls `onReady(true)` optimistically so the submit button is
 *      never disabled during the silent probe. If the probe eventually
 *      fails, we flip the button back to "Waiting for server…" then.
 *
 * No-op on the happy path — returns null and the user never sees a
 * banner during a normal login.
 */
const CACHE_KEY = 'ievntur:backend-health';
const CACHE_TTL_MS = 60_000;                    // 1 min — enough to smooth a whole session
const SLOW_PROBE_MS = 800;                      // banner appears only if probe is slower than this

const BackendHealthBanner = ({ onReady }) => {
  const [status, setStatus] = useState(() => {
    // Read cache SYNCHRONOUSLY on first render so the initial paint is
    // clean and no "Checking…" banner ever flashes on the fast path.
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (cached && cached.ok && Date.now() - cached.at < CACHE_TTL_MS) {
        return 'ok';
      }
    } catch { /* ignore */ }
    return 'silent-probing';
  });
  const [attempt, setAttempt] = useState(0);
  const [showReady, setShowReady] = useState(false);
  const slowTimerRef = useRef(null);

  const probe = useCallback(async () => {
    try {
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
      const res = await fetch(`${baseUrl}/api/health`, {
        method: 'GET',
        cache: 'no-store',
        signal: AbortSignal.timeout(5000),
      });
      if (res.ok) {
        try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ok: true, at: Date.now() })); } catch { /* ignore */ }
        if (slowTimerRef.current) { clearTimeout(slowTimerRef.current); slowTimerRef.current = null; }
        setStatus(prev => {
          if (prev === 'down') setShowReady(true);
          return 'ok';
        });
        if (onReady) onReady(true);
        return true;
      }
      throw new Error(`status ${res.status}`);
    } catch (e) {
      if (slowTimerRef.current) { clearTimeout(slowTimerRef.current); slowTimerRef.current = null; }
      try { sessionStorage.removeItem(CACHE_KEY); } catch { /* ignore */ }
      setStatus('down');
      if (onReady) onReady(false);
      return false;
    }
  }, [onReady]);

  useEffect(() => {
    // Fast-path — cache said ok, we're done.
    if (status === 'ok') {
      if (onReady) onReady(true);
      return () => {};
    }
    // Optimistically report ready so the submit button is not disabled
    // during the silent probe. Iter-314 P0 — the user must be able to
    // click Sign In immediately.
    if (onReady) onReady(true);
    // Start a slow-probe timer. If probe finishes before this fires,
    // we cleanup and the user never sees the banner.
    slowTimerRef.current = setTimeout(() => {
      setStatus(prev => (prev === 'silent-probing' ? 'checking' : prev));
    }, SLOW_PROBE_MS);
    probe();
    return () => {
      if (slowTimerRef.current) { clearTimeout(slowTimerRef.current); slowTimerRef.current = null; }
    };
  }, []);

  useEffect(() => {
    if (status !== 'down') return;
    // 4s, 4s, 8s, 16s, 30s cap.
    const delays = [4000, 4000, 8000, 16000, 30000];
    const t = setTimeout(() => {
      setAttempt(a => a + 1);
      probe();
    }, delays[Math.min(attempt, delays.length - 1)]);
    return () => clearTimeout(t);
  }, [status, attempt, probe]);

  useEffect(() => {
    if (!showReady) return;
    const t = setTimeout(() => setShowReady(false), 2500);
    return () => clearTimeout(t);
  }, [showReady]);

  // Fast path — happy state, no banner.
  if (status === 'ok' && !showReady) return null;
  // Silent probe running — the banner NEVER shows unless the slow-timer fires.
  if (status === 'silent-probing') return null;

  if (showReady) {
    return (
      <div
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-full
                   bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm
                   font-medium shadow-md flex items-center gap-2 animate-in fade-in-0"
        data-testid="backend-ready-banner"
      >
        <CheckCircle2 className="w-4 h-4" />
        Connected — you can sign in now
      </div>
    );
  }

  // status === 'checking' (slow probe) OR 'down'
  const isDown = status === 'down';
  return (
    <div
      className={`fixed top-0 inset-x-0 z-50 ${isDown ? 'bg-amber-50 border-b border-amber-200 text-amber-900' : 'bg-slate-50 border-b border-slate-200 text-slate-700'}`}
      role="status"
      aria-live="polite"
      data-testid={isDown ? 'backend-down-banner' : 'backend-checking-banner'}
    >
      <div className="max-w-md mx-auto px-4 py-2.5 flex items-center gap-2 text-sm">
        <AlertTriangle className="w-4 h-4 shrink-0" />
        <span className="flex-1">
          {isDown ? (
            <>
              Reconnecting to iEvntür… we&apos;re waking the backend.
              {attempt > 0 && <span className="opacity-60 ml-1">(retry {attempt + 1})</span>}
            </>
          ) : (
            <>Backend is taking a moment. You can still try signing in — we&apos;ll keep retrying in the background.</>
          )}
        </span>
      </div>
    </div>
  );
};

export default BackendHealthBanner;
